import React, { useCallback, useState } from "react";

import { TAlbum, TTrack } from "@store/actions/track";
import { useAppSelector } from "@store/hooks";
import instance from "@store/middleware/api";
import { getMySelector } from "@store/selectors/getMeSelector";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";

import * as S from "./styles";

export enum EDownloadModalType {
  Song = "Song",
  Album = "Album",
}

interface IDownloadPageProps {
  handleNext: (downloadStatus: "started" | "complete" | "error") => void;
  onOpenLicense?: () => void;
  item?: TAlbum;
  song?: TTrack;
  type: EDownloadModalType;
  name?: string;
  cover?: string;
  // Force WAV unlocked regardless of plan/hasWav — used when the track is
  // already licensed (e.g. opened from the Orders page, where the buyer owns it).
  wavAllowed?: boolean;
}

const DownloadPage: React.FC<IDownloadPageProps> = ({ handleNext, onOpenLicense, item, song, type, cover, wavAllowed }) => {
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();
  // `getMySelector` returns `null` when no user is logged in (the auth
  // slice is null until after `/users/my` lands). Guard against that so
  // opening the download modal as a guest doesn't crash the whole app.
  const me = useAppSelector(getMySelector);
  const hasWav = !!me?.hasWav;

  const isAlbum = type === EDownloadModalType.Album;
  const media = isAlbum ? item : song;
  const canDownloadWav =
    wavAllowed ||
    hasWav ||
    accountType === AccountType.SUBSCRIPTION ||
    accountType === AccountType.MUSIC_CREATOR;

  const coverUrl = cover || item?.largeCoverUrl || item?.mediumCoverUrl;
  const isFree = accountType === AccountType.FREE;

  // Artist/composer name — resolved once for the on-screen label AND the
  // download filename (composer can be an {name} object or a plain string).
  const composerName = song?.composer
    ? (typeof song.composer === "object" ? (song.composer as any).name : String(song.composer))
    : (item as any)?.composer
      ? (typeof (item as any).composer === "object" ? (item as any).composer.name : String((item as any).composer))
      : "";

  const [wavShaking, setWavShaking] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (isLowQuality: boolean) => {
    if (downloading) return;
    setDownloading(true);
    handleNext("started");

    try {
      const trackId = song?._id || (item as any)?._id;
      if (!trackId) throw new Error("No track ID");

      // Get signed URL from API
      const resp = await instance.get(`/downloads/track?id=${trackId}&isLowQuality=${isLowQuality}`);
      const url = isLowQuality ? resp.data.lowQualityFileUrl : resp.data.fileUrl;
      if (!url) throw new Error("No download URL");

      // Fetch file as blob to track completion
      const fileResp = await fetch(url);
      if (!fileResp.ok) throw new Error("Download failed");
      const blob = await fileResp.blob();

      // Trigger browser download from blob
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      // Filename: "Artist - Title.ext" (fall back to just the title when the
      // artist is unknown). Strip characters that are illegal in filenames.
      const title = media?.name || "track";
      const base = composerName ? `${composerName} - ${title}` : title;
      const safeName = base.replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, " ").trim() || "track";
      a.download = `${safeName}.${isLowQuality ? "mp3" : "wav"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      handleNext("complete");
    } catch {
      handleNext("error");
    }
  };

  const handleLockedWavClick = useCallback(() => {
    if (onOpenLicense) {
      onOpenLicense();
    } else {
      setWavShaking(false);
      requestAnimationFrame(() => setWavShaking(true));
      setTimeout(() => setWavShaking(false), 500);
    }
  }, [onOpenLicense]);

  const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#9cd3fd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "6px" }}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "28px" }}>
      <S.StepLabel>Step 1</S.StepLabel>

      {coverUrl ? (
        <S.TrackCover src={coverUrl} alt={media?.name} />
      ) : (
        <S.TrackCoverPlaceholder>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
        </S.TrackCoverPlaceholder>
      )}

      <div style={{ textAlign: "center" }}>
        <S.TrackName style={{ textAlign: "center" }}>{media?.name}</S.TrackName>
        <S.TrackArtist style={{ textAlign: "center", marginTop: "4px", color: "#fff" }}>
          {composerName}
        </S.TrackArtist>
      </div>

      <S.FormatDivider />

      <div style={{ width: "100%" }}>
        <S.FormatLabel>Choose Audio Format</S.FormatLabel>
        <S.FormatButtons>
          <S.FormatBtn disabled={downloading} onClick={() => handleDownload(true)} style={downloading ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
            <DownloadIcon />
            <S.FormatName>MP3</S.FormatName>
            {isFree && !wavAllowed && <S.FormatSize style={{ color: "#34d399" }}>Free</S.FormatSize>}
          </S.FormatBtn>

          {canDownloadWav ? (
            <S.FormatBtn disabled={downloading} onClick={() => handleDownload(false)} style={downloading ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
              <DownloadIcon />
              <S.FormatName>WAV</S.FormatName>
            </S.FormatBtn>
          ) : (
            <S.FormatBtn $locked $shaking={wavShaking} onClick={handleLockedWavClick}>
              <DownloadIcon />
              <S.FormatName>WAV</S.FormatName>
              <S.TierBadge $variant="premium">Premium or Buy</S.TierBadge>
            </S.FormatBtn>
          )}
        </S.FormatButtons>
      </div>
    </div>
  );
};

export default DownloadPage;
